import React, { useEffect, useRef, useState } from 'react';
import { LTText } from '@lambdatestincprivate/lt-components';
import { CheckIcon } from '@primer/octicons-react';
import styles from './AIRca.module.css';

/**
 * Time-driven progressive loader for AI RCA generation.
 *
 * RCA generation has no backend progress signal and is long-tailed: most runs
 * finish around ~1 min, but some take 2+ min. A fixed-duration script would
 * either finish early (then freeze) or lie. So the bar is decoupled from the
 * stage labels and follows an exponential-approach curve:
 *
 *     progress = ASYMPTOTE × (1 − e^(−t / TAU))
 *
 * Tuned so it reads ~95% at 60s, ~98% at 2 min, ~99% at 3 min — it ALWAYS keeps
 * inching and NEVER reaches 100% on its own. When the data arrives the parent
 * unmounts this loader, so there is no "100% but still loading" lie.
 *
 * Past the estimate window the loader enters an "extended" phase: the final
 * stage relabels to a reassuring message and cycles sub-captions so motion
 * continues without faking completion. A hard run that exhausts the poll budget
 * is handled by useRCA's existing timeout/error path (the loader unmounts).
 *
 * Accessibility: the bar is a role="progressbar" with live aria-valuenow; the
 * active label is announced via aria-live. prefers-reduced-motion drops the
 * eased transition and spinner animation.
 */

// Stage labels advance at these cumulative elapsed-ms thresholds.
const STAGES = [
  { label: 'Collecting test logs & artifacts', atMs: 0 },
  { label: 'Analyzing failure & stack traces', atMs: 8000 },
  { label: 'Correlating events on the timeline', atMs: 20000 },
  { label: 'Running visual / AI root-cause analysis', atMs: 35000 },
  { label: 'Finalizing insights', atMs: 50000 }
];

// Exponential-approach curve params. ASYMPTOTE caps below 100; TAU≈18.7s yields
// ~95% at 60s. Verified: 99·(1−e^(−60/18.7)) ≈ 95%, ≈98% at 120s, ≈99% at 180s.
const ASYMPTOTE = 99;
const TAU_MS = 18700;

// Past this elapsed time we consider the run "extended" (overrunning the
// estimate) and switch to reassuring, cycling messaging.
const EXTENDED_AFTER_MS = 60000;
const SUBCAPTION_MS = 4000;
const EXTENDED_PREFIX = 'Taking a little longer than usual';
const EXTENDED_SUBCAPTIONS = [
  'cross-checking screenshots',
  'reconciling failure categories',
  'finalizing insights'
];

const TICK_MS = 200;

const stageIndexForElapsed = (elapsedMs) => {
  let idx = 0;
  for (let i = 0; i < STAGES.length; i += 1) {
    if (elapsedMs >= STAGES[i].atMs) idx = i;
  }
  return idx;
};

const RCAProgressiveLoader = () => {
  const [progress, setProgress] = useState(0);
  const [activeStage, setActiveStage] = useState(0);
  const [extended, setExtended] = useState(false);
  const [subCaption, setSubCaption] = useState(0);

  useEffect(() => {
    const start = Date.now();

    const tick = () => {
      const elapsed = Date.now() - start;
      setProgress(ASYMPTOTE * (1 - Math.exp(-elapsed / TAU_MS)));
      setActiveStage(stageIndexForElapsed(elapsed));
      if (elapsed >= EXTENDED_AFTER_MS) {
        setExtended(true);
        const overrun = elapsed - EXTENDED_AFTER_MS;
        setSubCaption(Math.floor(overrun / SUBCAPTION_MS) % EXTENDED_SUBCAPTIONS.length);
      }
    };

    tick();
    const id = setInterval(tick, TICK_MS);
    return () => clearInterval(id);
  }, []);

  const rounded = Math.round(progress);

  return (
    <div className={styles.progLoader}>
      {/* Indented to the test-name text axis so the bar + "Generating RCA"
          label line up with the title and the stage names (markers sit in the
          left gutter, like the status icon). */}
      <div className={styles.progBarSection}>
        <div className={styles.progBarLabelRow}>
          <span className={styles.progShimmerText}>
            {`Generating RCA (${rounded}%)`}
          </span>
          <span className={styles.progShimmerTextMuted}>
            Please wait while we generate RCA
          </span>
        </div>

        <div
          className={styles.progBarTrack}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={rounded}
          aria-label="Generating root cause analysis"
        >
          <div
            className={styles.progBarFill}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <ol className={styles.progStages} aria-live="polite">
        {STAGES.map((stage, i) => {
          const isLast = i === STAGES.length - 1;
          // In the extended phase the final stage stays active and carries the
          // reassuring, cycling message; earlier stages all read as done.
          const isDone = extended ? !isLast : i < activeStage;
          const isActive = extended ? isLast : i === activeStage;

          let label = stage.label;
          if (isActive) {
            label = extended && isLast
              ? `${EXTENDED_PREFIX} — ${EXTENDED_SUBCAPTIONS[subCaption]}…`
              : `${stage.label}…`;
          }

          // Active stage shimmers (animated theme gradient); done/pending render
          // as greyed LTText. Colour is set on the LTText itself because LTText
          // applies its own colour and would override a parent <li> colour.
          const labelColor = isDone ? '#57606a' : '#afb8c1';
          const labelNode = isActive ? (
            <span className={styles.progShimmerText}>{label}</span>
          ) : (
            <LTText
              text={label}
              variant={LTText.TextVariant.SMALL_REGULAR}
              style={{ color: labelColor }}
            />
          );

          // Marker per state: done → green tick, active → green rotating ring
          // (matches the bar), pending → empty greyed circle. All occupy the
          // same 16px gutter slot so step text stays aligned on the 48px axis.
          // Done steps also green the connector line that interlinks the ticks.
          let marker;
          if (isDone) {
            marker = (
              <span className={styles.progDotDone} aria-hidden="true">
                <CheckIcon size={12} />
              </span>
            );
          } else if (isActive) {
            marker = <span className={styles.progSpinner} aria-hidden="true" />;
          } else {
            marker = <span className={styles.progDotPending} aria-hidden="true" />;
          }

          const liClass = isDone
            ? `${styles.progStage} ${styles.progStageDone}`
            : styles.progStage;

          return (
            <li key={stage.label} className={liClass}>
              {marker}
              {labelNode}
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default RCAProgressiveLoader;
